import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Truck, MapPin, Clock, Instagram, Facebook, Twitter, ArrowRight, Star, Crown, Zap, CalendarPlus, Monitor, Send } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const API = "/api";

const FoodTruckCard = ({ truck }) => {
  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'twitter': return Twitter;
      default: return null;
    }
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="group rounded-sm overflow-hidden bg-card border border-border card-hover"
      data-testid={`food-truck-card-${truck.id}`}
    >
      <div className="aspect-video relative">
        <img
          src={truck.image_url || 'https://images.unsplash.com/photo-1565299585323-38d6b0865b47?w=800'}
          alt={truck.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          <span className="px-3 py-1 bg-secondary text-secondary-foreground text-xs font-medium rounded-sm">
            {truck.cuisine}
          </span>
          {truck.subscription_tier === 'featured' && (
            <Badge className="bg-yellow-600/90 text-white text-[10px] gap-1 border-0"><Star size={10} />Featured</Badge>
          )}
          {truck.subscription_tier === 'premium' && (
            <Badge className="bg-purple-600/90 text-white text-[10px] gap-1 border-0"><Crown size={10} />Premium</Badge>
          )}
          {truck.subscription_tier === 'earlybird' && (
            <Badge className="bg-emerald-600/90 text-white text-[10px] gap-1 border-0"><Zap size={10} />Early Bird</Badge>
          )}
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        <h3 className="font-heading text-xl font-bold">{truck.name}</h3>
        <p className="text-sm text-muted-foreground line-clamp-2">{truck.description}</p>
        
        {truck.location && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin size={14} className="text-primary" />
            <span>{truck.location}</span>
          </div>
        )}
        
        {truck.schedule && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock size={14} className="text-secondary" />
            <span>{truck.schedule}</span>
          </div>
        )}
        
        {truck.social_links && Object.keys(truck.social_links).length > 0 && (
          <div className="flex gap-2 pt-2">
            {Object.entries(truck.social_links).map(([platform, handle]) => {
              const Icon = getSocialIcon(platform);
              if (!Icon) return null;
              return (
                <Button
                  key={platform}
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => window.open(`https://${platform}.com/${handle.replace('@', '')}`, '_blank')}
                >
                  <Icon size={16} />
                </Button>
              );
            })}
          </div>
        )}
        
        {truck.subscription_tier && truck.subscription_tier !== 'free' && (
          <Link
            to={`/contact?subject=Book ${truck.name} for my event`}
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-orange-600/20 border border-orange-500/30 text-orange-400 text-xs font-bold hover:bg-orange-600/30 transition-colors"
            data-testid={`book-truck-${truck.id}`}
          >
            <CalendarPlus size={14} /> Book for Your Event
          </Link>
        )}
      </div>
    </motion.div>
  );
};

const FoodTrucks = () => {
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCuisine, setSelectedCuisine] = useState('all');
  const [showWebsiteForm, setShowWebsiteForm] = useState(false);
  const [webFormLoading, setWebFormLoading] = useState(false);
  const [webForm, setWebForm] = useState({ name: '', email: '', phone: '', business: '', details: '' });

  useEffect(() => {
    fetchTrucks();
  }, []);

  const fetchTrucks = async () => {
    try {
      const response = await axios.get(`${API}/food-trucks`);
      setTrucks(response.data);
    } catch (error) {
      console.error('Failed to fetch food trucks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWebsiteInquiry = async () => {
    if (!webForm.name || !webForm.email || !webForm.business) {
      toast.error('Please fill in name, email, and business name');
      return;
    }
    setWebFormLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: webForm.name,
        email: webForm.email,
        phone: webForm.phone,
        subject: `Website Inquiry: ${webForm.business}`,
        message: `Website/Web Services Inquiry (from Food Trucks page)\n\nBusiness: ${webForm.business}\nPhone: ${webForm.phone || 'Not provided'}\n\n${webForm.details}`,
        category: 'website_inquiry',
      });
      toast.success("We got you! We'll be in touch soon.");
      setShowWebsiteForm(false);
      setWebForm({ name: '', email: '', phone: '', business: '', details: '' });
    } catch {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setWebFormLoading(false);
    }
  };

  const cuisines = ['all', ...new Set(trucks.map(t => t.cuisine))];
  const filteredTrucks = selectedCuisine === 'all' 
    ? trucks 
    : trucks.filter(t => t.cuisine === selectedCuisine);

  return (
    <div className="space-y-8" data-testid="food-trucks-page">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-4"
      >
        <div className="flex items-center gap-2">
          <Truck size={16} className="text-secondary" />
          <span className="text-xs text-muted-foreground tracking-widest uppercase">Twin Cities Food Scene</span>
        </div>
        
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">Local Food Trucks</h1>
        <p className="text-muted-foreground max-w-2xl">
          Discover the best food trucks in Minneapolis and St. Paul. From gourmet tacos to Scandinavian waffles.
        </p>
      </motion.div>

      {/* Food Truck Registration CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="rounded-xl border border-orange-500/30 bg-gradient-to-r from-orange-900/20 via-card to-amber-900/20 p-5 flex flex-col sm:flex-row items-center gap-4 sm:justify-between"
        data-testid="food-truck-register-cta"
      >
        <div className="text-center sm:text-left">
          <p className="font-bold text-base">Roll with the Twin Cities!</p>
          <p className="text-sm text-muted-foreground">Register your food truck and reach hungry locals at shows and events.</p>
        </div>
        <Link
          to="/pricing"
          className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-orange-600 hover:bg-orange-500 text-white font-bold text-sm transition-colors"
          data-testid="food-truck-register-link"
        >
          View Truck Plans <ArrowRight size={16} />
        </Link>
      </motion.div>

      {/* Website Inquiry CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        onClick={() => setShowWebsiteForm(true)}
        className="rounded-xl border border-blue-500/30 bg-gradient-to-r from-blue-900/20 via-card to-cyan-900/20 p-5 flex flex-col sm:flex-row items-center gap-4 sm:justify-between cursor-pointer hover:border-blue-400/50 transition-colors"
        data-testid="website-cta"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-500/20 border border-blue-500/30 flex items-center justify-center">
            <Monitor size={20} className="text-blue-400" />
          </div>
          <div className="text-center sm:text-left">
            <p className="font-bold text-base">Need a website? We gotchu!</p>
            <p className="text-sm text-muted-foreground">Professional website creation & management for local businesses.</p>
          </div>
        </div>
        <span className="shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white font-bold text-sm transition-colors">
          Get Started <ArrowRight size={16} />
        </span>
      </motion.div>

      {/* Filter */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="flex items-center gap-4"
      >
        <Select value={selectedCuisine} onValueChange={setSelectedCuisine}>
          <SelectTrigger className="w-48" data-testid="cuisine-filter">
            <SelectValue placeholder="Filter by cuisine" />
          </SelectTrigger>
          <SelectContent>
            {cuisines.map((cuisine) => (
              <SelectItem key={cuisine} value={cuisine}>
                {cuisine === 'all' ? 'All Cuisines' : cuisine}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <span className="text-sm text-muted-foreground">
          {filteredTrucks.length} truck{filteredTrucks.length !== 1 ? 's' : ''} found
        </span>
      </motion.div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Skeleton key={i} className="aspect-[4/3]" />
          ))}
        </div>
      ) : filteredTrucks.length === 0 ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-16"
        >
          <Truck size={48} className="mx-auto text-muted-foreground mb-4" />
          <h3 className="font-heading text-xl font-bold mb-2">No food trucks found</h3>
          <p className="text-muted-foreground">Check back soon for more delicious options!</p>
        </motion.div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {filteredTrucks.map((truck, index) => (
            <motion.div
              key={truck.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
            >
              <FoodTruckCard truck={truck} />
            </motion.div>
          ))}
        </motion.div>
      )}

      {/* Website Inquiry Dialog */}
      <Dialog open={showWebsiteForm} onOpenChange={setShowWebsiteForm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Monitor size={20} className="text-blue-400" /> Need a website? We gotchu!</DialogTitle>
            <DialogDescription>Tell us about your business and we'll get back to you with a plan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ft_w_name" className="text-xs">Your Name *</Label>
                <Input id="ft_w_name" placeholder="Name" value={webForm.name} onChange={e => setWebForm({...webForm, name: e.target.value})} data-testid="ft-web-form-name" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ft_w_email" className="text-xs">Email *</Label>
                <Input id="ft_w_email" type="email" placeholder="you@email.com" value={webForm.email} onChange={e => setWebForm({...webForm, email: e.target.value})} data-testid="ft-web-form-email" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="ft_w_phone" className="text-xs">Phone</Label>
                <Input id="ft_w_phone" type="tel" placeholder="(612) 555-0000" value={webForm.phone} onChange={e => setWebForm({...webForm, phone: e.target.value})} data-testid="ft-web-form-phone" />
              </div>
              <div className="space-y-1">
                <Label htmlFor="ft_w_biz" className="text-xs">Business Name *</Label>
                <Input id="ft_w_biz" placeholder="Your business" value={webForm.business} onChange={e => setWebForm({...webForm, business: e.target.value})} data-testid="ft-web-form-business" />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ft_w_details" className="text-xs">What do you need?</Label>
              <Textarea id="ft_w_details" placeholder="New website, redesign, online store, social media help..." rows={3} value={webForm.details} onChange={e => setWebForm({...webForm, details: e.target.value})} data-testid="ft-web-form-details" />
            </div>
            <Button onClick={handleWebsiteInquiry} disabled={webFormLoading} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold gap-2" data-testid="ft-web-form-submit">
              {webFormLoading ? 'Sending...' : 'Send Inquiry'}
              {!webFormLoading && <Send size={16} />}
            </Button>
            <p className="text-[11px] text-center text-muted-foreground">We'll get back to you within 24 hours.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FoodTrucks;
